import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  Clock,
  MapPin,
  AlertCircle,
  CheckCircle,
  Loader,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const BookCaseAppointmentModal = ({ caseData, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [formData, setFormData] = useState({
    caseId: caseData.id,
    accountId: caseData.accountId,
    title: `Service Visit for Case #${caseData.caseNumber}`,
    description: caseData.subject || "",
    startTime: "",
    endTime: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    location: "",
    locationLat: null,
    locationLng: null,
    locationPlaceId: null,
    type: "site_visit",
    priority: caseData.priority?.toLowerCase() || "medium",
    assignedTo: "",
    visibility: "team",
    installationBookedAs: "",
    installationOutcome: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viabilityCheck, setViabilityCheck] = useState(null);
  const [checkingViability, setCheckingViability] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [locationInput, setLocationInput] = useState("");

  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);

    const endTime = new Date(tomorrow);
    endTime.setHours(11, 0, 0, 0);

    setFormData((prev) => ({
      ...prev,
      startTime: tomorrow.toISOString().slice(0, 16),
      endTime: endTime.toISOString().slice(0, 16),
    }));

    if (caseData.account) {
      const accountAddress = buildAccountAddress(caseData.account);
      if (accountAddress) {
        setLocationInput(accountAddress);
        setFormData((prev) => ({
          ...prev,
          location: accountAddress,
        }));
        handleLocationBlur(accountAddress);
      }
    }

    fetchEngineeringTeamMembers();
  }, []);

  const buildAccountAddress = (account) => {
    if (!account) return "";

    const parts = [
      account.shippingStreet || account.billingStreet,
      account.shippingCity || account.billingCity,
      account.shippingState || account.billingState,
      account.shippingPostalCode || account.billingPostalCode,
      account.shippingCountry || account.billingCountry,
    ].filter(Boolean);

    return parts.join(", ");
  };

  const fetchEngineeringTeamMembers = async () => {
    try {
      setLoadingUsers(true);
      const response = await apiService.getEngineeringTeamMembers();

      if (response.success) {
        const members = response.members || [];
        const uniqueMembers = members
          .filter((m) => m.user && m.status === "active")
          .map((m) => ({
            id: m.user.id,
            name: m.user.name,
            email: m.user.email,
            profileImageUrl: m.user.profileImageUrl,
            role: m.user.role,
          }));

        setUsers(uniqueMembers);

        if (uniqueMembers.length === 0) {
          setError("No active engineering team members found");
        }
      } else {
        setError(response.message || "Failed to load engineering team members");
      }
    } catch (error) {
      console.error("Error fetching engineering team members:", error);
      setError("Failed to load engineering team members");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleLocationBlur = async (address = locationInput) => {
    if (!address || !address.trim()) {
      setFormData((prev) => ({
        ...prev,
        location: "",
        locationLat: null,
        locationLng: null,
        locationPlaceId: null,
      }));
      return;
    }

    setGeocoding(true);
    try {
      const response = await apiService.geocodeAddress(address);

      if (response.success && response.location) {
        setFormData((prev) => ({
          ...prev,
          location: response.location.formattedAddress,
          locationLat: response.location.lat,
          locationLng: response.location.lng,
          locationPlaceId: response.location.placeId,
        }));
        setLocationInput(response.location.formattedAddress);
      } else {
        setFormData((prev) => ({
          ...prev,
          location: address,
          locationLat: null,
          locationLng: null,
          locationPlaceId: null,
        }));
        setError("Could not verify location address. Proceeding anyway.");
        setTimeout(() => setError(""), 3000);
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      setFormData((prev) => ({
        ...prev,
        location: address,
      }));
    } finally {
      setGeocoding(false);
    }
  };

  useEffect(() => {
    const checkViability = async () => {
      if (!formData.startTime || !formData.endTime || !formData.assignedTo) {
        setViabilityCheck(null);
        return;
      }

      setCheckingViability(true);
      setError("");

      try {
        const response = await apiService.checkAppointmentViability({
          ...formData,
        });

        setViabilityCheck(response);

        if (!response.viable) {
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Error checking viability:", error);
        setViabilityCheck(null);
      } finally {
        setCheckingViability(false);
      }
    };

    const timeoutId = setTimeout(checkViability, 800);
    return () => clearTimeout(timeoutId);
  }, [
    formData.startTime,
    formData.endTime,
    formData.location,
    formData.locationLat,
    formData.locationLng,
    formData.assignedTo,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (viabilityCheck && !viabilityCheck.viable && !formData.force) {
      setShowSuggestions(true);
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.createAppointmentFromCase(formData);

      if (response.success) {
        onSuccess(response.appointment);
      }
    } catch (error) {
      console.error("Error booking appointment:", error);

      if (error.response?.status === 409 && error.response?.data?.suggestions) {
        setViabilityCheck(error.response.data);
        setShowSuggestions(true);
      } else {
        setError(error.response?.data?.message || "Failed to book appointment");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "location") {
      setLocationInput(value);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      force: name === "force" ? prev.force : false,
    }));
  };

  const applySuggestion = (suggestion) => {
    setFormData((prev) => ({
      ...prev,
      startTime: new Date(suggestion.startTime).toISOString().slice(0, 16),
      endTime: new Date(suggestion.endTime).toISOString().slice(0, 16),
      force: false,
    }));
    setShowSuggestions(false);
    setViabilityCheck(null);
  };

  const forceCreate = () => {
    setFormData((prev) => ({ ...prev, force: true }));
    setShowSuggestions(false);
    handleSubmit(new Event("submit"));
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Shared helpers
  const inputCls = `w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 ${
    isDarkMode
      ? "bg-gray-900/50 border-gray-700/50 text-white placeholder-gray-500"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const selectCls = `${inputCls} appearance-none cursor-pointer`;

  const labelCls = `block text-sm font-medium mb-2.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;

  const chevronSvg = (color = "%239CA3AF") =>
    `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='${color}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div
        className={`rounded-2xl shadow-2xl border w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-gradient-to-br from-[#0C0E12] to-[#0A0C10] border-gray-800/50"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`relative border-b p-8 ${
            isDarkMode
              ? "bg-gradient-to-r from-[#0C0E12] via-[#0E1015] to-[#0C0E12] border-gray-800/50"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          {isDarkMode && (
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent pointer-events-none"></div>
          )}
          <div className="relative flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className={`w-10 h-10 rounded-xl border flex items-center justify-center ${
                    isDarkMode
                      ? "bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/20"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <Calendar
                    size={20}
                    className={isDarkMode ? "text-blue-400" : "text-blue-500"}
                  />
                </div>
                <h2
                  className={`text-2xl font-semibold tracking-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Book Service Appointment
                </h2>
              </div>
              <p
                className={`text-sm ml-13 font-light ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Case #{caseData.caseNumber} • {caseData.subject}
              </p>
            </div>
            <button
              onClick={onClose}
              className={`group w-9 h-9 rounded-lg border flex items-center justify-center transition-all duration-200 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-700/50 border-gray-700/50 hover:border-gray-600/50 text-gray-400 hover:text-white"
                  : "bg-white hover:bg-gray-100 border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-900"
              }`}
            >
              <X
                size={18}
                className="group-hover:rotate-90 transition-transform duration-200"
              />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Alert */}
            {error && (
              <div
                className={`border rounded-xl p-4 animate-slideDown ${
                  isDarkMode
                    ? "bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/30"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? "bg-red-500/20" : "bg-red-100"}`}
                  >
                    <AlertCircle
                      size={16}
                      className={isDarkMode ? "text-red-400" : "text-red-500"}
                    />
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-sm font-medium ${isDarkMode ? "text-red-300" : "text-red-700"}`}
                    >
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Case Information */}
            {caseData.account && (
              <div
                className={`rounded-xl p-5 border ${
                  isDarkMode
                    ? "bg-gradient-to-br from-gray-800/30 to-gray-900/20 border-gray-700/40"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-xl border flex items-center justify-center flex-shrink-0 ${
                      isDarkMode
                        ? "bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/20"
                        : "bg-purple-50 border-purple-200"
                    }`}
                  >
                    <AlertTriangle
                      size={20}
                      className={
                        isDarkMode ? "text-purple-400" : "text-purple-500"
                      }
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`font-semibold text-lg mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {caseData.account.name}
                    </h3>
                    <div className="space-y-1.5">
                      <p
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Case Type: {caseData.type || "Not specified"}
                      </p>
                      <p
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Priority: {caseData.priority}
                      </p>
                      {caseData.account.phone && (
                        <p
                          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Phone: {caseData.account.phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label className={labelCls}>
                Appointment Title
                <span className="text-red-400 ml-1">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className={inputCls}
                placeholder="Enter appointment title"
              />
            </div>

            {/* Description */}
            <div>
              <label className={labelCls}>Service Notes</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className={`${inputCls} resize-none`}
                placeholder="Service requirements, parts needed, special instructions..."
              />
            </div>

            {/* Time Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>
                  Start Time
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <Calendar size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="datetime-local"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleChange}
                    required
                    className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-gray-900/50 border-gray-700/50 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>
                  End Time
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <Clock size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="datetime-local"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleChange}
                    required
                    className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-gray-900/50 border-gray-700/50 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Assigned Engineer */}
            <div>
              <label className={labelCls}>
                Assign to Engineer
                <span className="text-red-400 ml-1">*</span>
              </label>
              {loadingUsers ? (
                <div
                  className={`w-full px-4 py-3 border rounded-xl flex items-center justify-center gap-2 ${
                    isDarkMode
                      ? "bg-gray-900/50 border-gray-700/50 text-gray-400"
                      : "bg-gray-50 border-gray-200 text-gray-500"
                  }`}
                >
                  <Loader className="animate-spin" size={16} />
                  <span className="text-sm">Loading engineers...</span>
                </div>
              ) : (
                <>
                  <select
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleChange}
                    required
                    disabled={users.length === 0}
                    className={`${selectCls} disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={{
                      backgroundImage: users.length > 0 ? chevronSvg() : "none",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 1rem center",
                    }}
                  >
                    <option value="">Select engineer</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} {user.email && `(${user.email})`}
                      </option>
                    ))}
                  </select>
                  {users.length === 0 && (
                    <p
                      className={`text-xs mt-2 flex items-center gap-1.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      <AlertCircle size={12} />
                      No engineering team members available
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Location */}
            <div>
              <label className={labelCls}>Service Location</label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <MapPin size={16} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  name="location"
                  value={locationInput}
                  onChange={handleChange}
                  onBlur={() => handleLocationBlur()}
                  className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 ${
                    isDarkMode
                      ? "bg-gray-900/50 border-gray-700/50 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                  placeholder="Enter service address"
                />
              </div>
              {formData.locationLat && formData.locationLng && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400">
                    <CheckCircle size={12} />
                    <span>Location verified</span>
                  </div>
                </div>
              )}
              {!formData.locationLat && locationInput && !geocoding && (
                <p className="text-xs text-yellow-400/80 mt-2 flex items-center gap-1.5">
                  <AlertCircle size={12} />
                  Location not verified - travel time may be inaccurate
                </p>
              )}
              {caseData.account && (
                <p
                  className={`text-xs mt-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  💡 Auto-populated from account address. Press Tab to verify
                  location.
                </p>
              )}
            </div>

            {/* Type */}
            <div>
              <label className={labelCls}>Appointment Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className={selectCls}
                style={{
                  backgroundImage: chevronSvg(),
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                }}
              >
                <option value="site_visit">Site Visit</option>
                <option value="meeting">Online Meeting</option>
                <option value="consultation">Consultation</option>
                <option value="follow_up">Follow Up</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className={labelCls}>Priority Level</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className={selectCls}
                style={{
                  backgroundImage: chevronSvg(),
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* Installation Booked As */}
            <div>
              <label className={labelCls}>
                Installation Booked As
                <span className="text-red-400 ml-1">*</span>
              </label>
              <select
                name="installationBookedAs"
                value={formData.installationBookedAs}
                onChange={handleChange}
                className={selectCls}
                style={{
                  backgroundImage: chevronSvg(),
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                }}
                required
              >
                <option value="">Select type</option>
                <option value="new_install">New Install</option>
                <option value="base_install">Base Install</option>
                <option value="revisit_for_support">Revisit for Support</option>
                <option value="broadband_connection">
                  Broadband Connection
                </option>
                <option value="revisit_install_problem">
                  Revisit Install Problem
                </option>
                <option value="webex_migration">Webex Migration</option>
              </select>
            </div>

            {/* Installation Outcome - Read Only */}
            <div>
              <label className={labelCls}>Installation Outcome</label>
              <select
                name="installationOutcome"
                value={formData.installationOutcome}
                onChange={handleChange}
                disabled
                className={`w-full px-4 py-3 border rounded-xl cursor-not-allowed opacity-60 appearance-none ${
                  isDarkMode
                    ? "bg-gray-900/30 border-gray-700/30 text-gray-500"
                    : "bg-gray-100 border-gray-200 text-gray-400"
                }`}
                style={{
                  backgroundImage: chevronSvg("%236B7280"),
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                }}
              >
                <option value="">Not set</option>
                <option value="new_install">New Install</option>
                <option value="base_install">Base Install</option>
                <option value="revisit_for_support">Revisit for Support</option>
                <option value="broadband_connection">
                  Broadband Connection
                </option>
                <option value="revisit_install_problem">
                  Revisit Install Problem
                </option>
                <option value="webex_migration">Webex Migration</option>
              </select>
              <p
                className={`text-xs mt-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                This field will be set after the appointment is completed
              </p>
            </div>

            {/* Geocoding Status */}
            {geocoding && (
              <div
                className={`border rounded-xl p-4 animate-pulse ${
                  isDarkMode
                    ? "bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/30"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? "bg-blue-500/20" : "bg-blue-100"}`}
                  >
                    <Loader className="animate-spin text-blue-400" size={16} />
                  </div>
                  <span
                    className={`text-sm font-medium ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
                  >
                    Verifying location address...
                  </span>
                </div>
              </div>
            )}

            {/* Viability Checking */}
            {checkingViability && (
              <div
                className={`border rounded-xl p-4 ${
                  isDarkMode
                    ? "bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/30"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? "bg-blue-500/20" : "bg-blue-100"}`}
                  >
                    <Loader className="animate-spin text-blue-400" size={16} />
                  </div>
                  <span
                    className={`text-sm font-medium ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
                  >
                    Checking engineer's schedule and travel time...
                  </span>
                </div>
              </div>
            )}

            {/* Viability Status */}
            {viabilityCheck && !checkingViability && (
              <div
                className={`rounded-xl p-5 border animate-slideDown ${
                  viabilityCheck.viable
                    ? isDarkMode
                      ? "bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/30"
                      : "bg-green-50 border-green-200"
                    : isDarkMode
                      ? "bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border-yellow-500/30"
                      : "bg-yellow-50 border-yellow-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      viabilityCheck.viable
                        ? "bg-green-500/20"
                        : "bg-yellow-500/20"
                    }`}
                  >
                    {viabilityCheck.viable ? (
                      <CheckCircle size={16} className="text-green-400" />
                    ) : (
                      <AlertCircle size={16} className="text-yellow-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div
                      className={`font-semibold mb-3 ${
                        viabilityCheck.viable
                          ? isDarkMode
                            ? "text-green-300"
                            : "text-green-700"
                          : isDarkMode
                            ? "text-yellow-300"
                            : "text-yellow-700"
                      }`}
                    >
                      {viabilityCheck.message}
                    </div>

                    {viabilityCheck.conflicts &&
                      viabilityCheck.conflicts.length > 0 && (
                        <div className="space-y-3">
                          {viabilityCheck.conflicts.map((conflict, index) => (
                            <div
                              key={index}
                              className={`rounded-lg p-4 border ${
                                isDarkMode
                                  ? "bg-black/20 border-white/5"
                                  : "bg-white border-gray-200"
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-6 h-6 rounded-md bg-yellow-500/20 flex items-center justify-center">
                                  <AlertCircle
                                    size={12}
                                    className="text-yellow-400"
                                  />
                                </div>
                                <span
                                  className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {conflict.type ===
                                  "insufficient_travel_time_before"
                                    ? "Not enough time from previous appointment"
                                    : "Not enough time to next appointment"}
                                </span>
                              </div>
                              {conflict.travelInfo && (
                                <div
                                  className={`text-xs space-y-2 pl-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  <div className="flex items-center justify-between py-1">
                                    <span
                                      className={
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }
                                    >
                                      Required:
                                    </span>
                                    <span className="font-medium">
                                      {conflict.requiredTravelTime} minutes
                                      (with buffer)
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between py-1">
                                    <span
                                      className={
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }
                                    >
                                      Available:
                                    </span>
                                    <span className="font-medium">
                                      {conflict.availableTime} minutes
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between py-1">
                                    <span
                                      className={
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }
                                    >
                                      Distance:
                                    </span>
                                    <span className="font-medium">
                                      {conflict.travelInfo.distance} •{" "}
                                      {conflict.travelInfo.estimatedTime}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* Suggestions */}
            {showSuggestions &&
              viabilityCheck?.suggestions &&
              viabilityCheck.suggestions.length > 0 && (
                <div
                  className={`border rounded-xl p-5 animate-slideDown ${
                    isDarkMode
                      ? "bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/30"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? "bg-blue-500/20" : "bg-blue-100"}`}
                    >
                      <Clock
                        size={16}
                        className={
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }
                      />
                    </div>
                    <h3
                      className={`font-semibold ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}
                    >
                      Suggested Alternative Times
                    </h3>
                  </div>
                  <div className="space-y-2.5 mb-4">
                    {viabilityCheck.suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => applySuggestion(suggestion)}
                        className={`w-full text-left border rounded-xl p-4 transition-all duration-200 group ${
                          isDarkMode
                            ? "bg-black/30 hover:bg-black/50 border-gray-700/50 hover:border-blue-500/50"
                            : "bg-white hover:bg-blue-50 border-gray-200 hover:border-blue-300"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-medium mb-1.5 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {formatDateTime(suggestion.startTime)}
                            </div>
                            <div
                              className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {suggestion.reason}
                            </div>
                            {suggestion.travelInfo && (
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                              >
                                Includes {suggestion.travelInfo.withBuffer}{" "}
                                travel time
                              </div>
                            )}
                          </div>
                          <div className="w-8 h-8 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 border border-blue-500/20 flex items-center justify-center transition-all duration-200">
                            <ArrowRight
                              size={14}
                              className="text-blue-400 group-hover:translate-x-0.5 transition-transform duration-200"
                            />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div
                    className={`flex gap-3 pt-4 border-t ${isDarkMode ? "border-gray-700/50" : "border-blue-200"}`}
                  >
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(false)}
                      className={`flex-1 px-4 py-2.5 border rounded-xl text-sm font-medium transition-all duration-200 ${
                        isDarkMode
                          ? "bg-gray-800/50 hover:bg-gray-800 border-gray-700/50 text-gray-300 hover:text-white"
                          : "bg-white hover:bg-gray-50 border-gray-300 text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Keep Original Time
                    </button>
                    <button
                      type="button"
                      onClick={forceCreate}
                      className="flex-1 px-4 py-2.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg shadow-yellow-600/20"
                    >
                      Schedule Anyway
                    </button>
                  </div>
                </div>
              )}
          </form>
        </div>

        {/* Footer Actions */}
        <div
          className={`border-t p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#0C0E12] to-[#0A0C10]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-5 py-3 border rounded-xl font-medium transition-all duration-200 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-800 border-gray-700/50 hover:border-gray-600/50 text-gray-300 hover:text-white"
                  : "bg-white hover:bg-gray-100 border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={
                loading ||
                loadingUsers ||
                checkingViability ||
                geocoding ||
                !formData.assignedTo
              }
              className="flex-1 px-5 py-3 text-white rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
              }}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Booking...</span>
                </>
              ) : (
                <>
                  <Calendar size={18} />
                  <span>Book Appointment</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default BookCaseAppointmentModal;
